import { pick } from 'es-toolkit/compat';
import { api } from '../../singletons';
import { BaseAdReporter } from '../BaseAdReporter';
import { AD_MEDIUM_TYPES, AD_SLOTS, clientAdTypes } from '../constants';
export class ClientSideAdReporter extends BaseAdReporter {
    #sessionMetadata;
    get errorLogPrefix() {
        return 'Client side ad break';
    }
    get adRequestUrl() {
        return this.#sessionMetadata?.adRequestUrl;
    }
    get sessionAdRequestId() {
        return this.#sessionMetadata?.adRequestId;
    }
    get biddingNetworks() {
        return this.#sessionMetadata?.biddingNetworks || [];
    }
    getAdErrorPrefix() {
        return 'Audio Video Ad';
    }
    set sessionMetadata(sessionMetadata) {
        this.#sessionMetadata = sessionMetadata;
        this.resetReportingState();
    }
    getAdsBiddingAdSlot() {
        // We eagerly request bids on page load, before any preroll request can be made, so there is no
        // session metadata available to pull slot data from. Fall back to preroll in that case.
        return this.#sessionMetadata ? this.#getAdSlot() : AD_SLOTS.PREROLL;
    }
    #getAdSlot(adType) {
        const clientAdType = adType || this.#sessionMetadata?.clientType;
        switch (clientAdType) {
            case clientAdTypes.preroll:
                return AD_SLOTS.PREROLL;
            case clientAdTypes.midroll:
                return AD_SLOTS.MIDROLL;
            default:
                return AD_SLOTS.UNSPECIFIED;
        }
    }
    #getAdMediumType(medium) {
        const adMedium = medium || this.#sessionMetadata?.medium;
        switch (adMedium) {
            case 'video':
                return AD_MEDIUM_TYPES.VIDEO;
            case 'audio':
                return AD_MEDIUM_TYPES.AUDIO;
            default:
                return AD_MEDIUM_TYPES.UNSPECIFIED;
        }
    }
    #getAdsBiddingParams(bidEvent) {
        const { requestId, unitId, network } = bidEvent;
        return {
            adRequestId: requestId,
            adUnitId: unitId,
            adNetworkName: network,
            adType: AD_MEDIUM_TYPES.VIDEO,
            // We eagerly request bids on page load, before any preroll request can be made, so there is no session metadata available
            // to pull the slot data from. Fall back to preroll for shared AdPlayer header bidding telemetry in that case.
            adSlot: this.#sessionMetadata ? this.#getAdSlot() : AD_SLOTS.PREROLL,
            adSize: '0x0',
        };
    }
    getAdParameters(adEvent) {
        const sourceAdEvent = this.getReportingAdEvent(adEvent);
        const { requestId, bitrate, totalAds, creativeId, adPosition = 1, destinationUrl, } = sourceAdEvent || {};
        const { adUnitId, adNetworkName, adRequestId, maxRolls = 0, maxTotalRolls = 0, } = this.#sessionMetadata || {};
        const playedAdsCount = this.getPlayedTrackedAdEventsCount();
        return {
            adUnitId: adUnitId || '',
            adNetworkName: adNetworkName || '',
            adRequestId: requestId || adRequestId || 'none',
            adSlot: this.#getAdSlot(),
            adType: this.#getAdMediumType(),
            adRequestDefaultTimer: true,
            midrollTimerInSeconds: 0,
            bitrate: bitrate || 0,
            noOfVideoaudiorollsPlayed: playedAdsCount,
            noOfVideoaudiorollsReceived: totalAds || 1,
            adCreativeId: creativeId || '',
            currentVideoaudiorollIdx: Math.max(adPosition - 1, 0),
            wasAdSkipped: this.wasAdSkipped,
            maxRolls: Math.max(maxRolls, 0),
            noOfVideoaudiorollRequested: Math.max(maxRolls, 0),
            maxTotalRolls: Math.max(maxTotalRolls, 0),
            isStreamPrebuffered: false,
            prebufferTimeMs: 0,
            props: {},
            wasAdCancelled: this.wasAdCancelled,
            destinationUrl: destinationUrl || '',
        };
    }
    reportEligibility({ medium, isEligible, isPlatformEligible, maxRolls, maxTotalRolls, }) {
        api.telemetry.reportAdsVideoAudioRollEligibilityDecided({
            adSlot: AD_SLOTS.PREROLL,
            adType: this.#getAdMediumType(medium),
            isPlatformEligible,
            isEligible,
            maxRolls: Math.max(maxRolls, 0),
            maxTotalRolls: Math.max(maxTotalRolls, 0),
        });
    }
    reportPlaybackCancelled({ duration = 0, reason }) {
        this.markAdCancelled();
        api.telemetry.reportAdsPlaybackCancelled({
            ...pick(this.getAdParameters(), [
                'adCreativeId',
                'adNetworkName',
                'adRequestId',
                'adSlot',
                'adType',
                'adUnitId',
                'currentVideoaudiorollIdx',
                'noOfVideoaudiorollsReceived',
            ]),
            duration,
            reason,
            props: {},
        });
    }
}
